import React, { useCallback, useEffect, useRef, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import orchestrationService from "../../services/orchestrationService";
import { OrchestrationSlot } from "../../types/orchestration";
import {
  floorToSlotUtc,
  formatSlotLabel,
  isExpectedSlotLate,
  isExpectedSlotPending,
  ONCALL_LATE_THRESHOLD_MIN,
} from "../../utils/orchestrationLayout";
import SlotRail from "./components/orchestration/SlotRail";
import SlotDag from "./components/orchestration/SlotDag";

const LIMIT_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50];
const POLL_MS = 30000;
const POLL_SECONDS = POLL_MS / 1000;

/** Admin page: per-slot performance-orchestration run graphs (PWA-2150). */
const OrchestrationStatusPage: React.FC = () => {
  const [slots, setSlots] = useState<OrchestrationSlot[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(10);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [secondsLeft, setSecondsLeft] = useState(POLL_SECONDS);

  // Monotonic request token: guards against an earlier (slow) limit-change or
  // poll response overwriting a newer one (CLAUDE.md section 6).
  const reqRef = useRef(0);
  // Current limit, readable by the heartbeat without re-arming the interval.
  const limitRef = useRef(limit);
  // Wall-clock time of the next scheduled auto-refresh.
  const nextRefreshRef = useRef(Date.now() + POLL_MS);

  const fetchSlots = useCallback(
    async (nextLimit: number, background = false) => {
      const reqId = ++reqRef.current;
      if (!background) setLoading(true);
      try {
        const res = await orchestrationService.getSlots(nextLimit);
        if (reqId !== reqRef.current) return; // stale success
        setSlots(res.items);
        setError(null);
        setSelectedKey((prev) => {
          if (prev && res.items.some((s) => s.slotUtc === prev)) return prev;
          return res.items[0]?.slotUtc ?? null;
        });
        setNowMs(Date.now());
      } catch (e) {
        if (reqId !== reqRef.current) return; // stale error
        setError(e instanceof Error ? e.message : "Failed to load orchestration status");
      } finally {
        if (reqId !== reqRef.current) return; // stale loading reset
        setLoading(false);
      }
    },
    []
  );

  const resetCountdown = useCallback(() => {
    nextRefreshRef.current = Date.now() + POLL_MS;
    setSecondsLeft(POLL_SECONDS);
  }, []);

  // Keep the latest limit available to the heartbeat without re-arming it.
  useEffect(() => {
    limitRef.current = limit;
  }, [limit]);

  // Initial load + refetch on limit change; restart the refresh countdown.
  useEffect(() => {
    fetchSlots(limit);
    resetCountdown();
  }, [limit, fetchSlots, resetCountdown]);

  const latest = slots && slots.length > 0 ? slots[0] : null;
  const isLive = latest?.phaseState.status === "in_progress";

  // One 1s heartbeat: advances the elapsed clock and the refresh countdown,
  // and triggers a background auto-refresh every POLL_MS (30s). Background
  // fetches keep the current view (never blank it) and still go through the
  // request-token guard in fetchSlots.
  useEffect(() => {
    const id = window.setInterval(() => {
      setNowMs(Date.now());
      const remainingMs = nextRefreshRef.current - Date.now();
      if (remainingMs <= 0) {
        nextRefreshRef.current = Date.now() + POLL_MS;
        setSecondsLeft(POLL_SECONDS);
        fetchSlots(limitRef.current, true);
      } else {
        setSecondsLeft(Math.ceil(remainingMs / 1000));
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [fetchSlots]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!slots || slots.length === 0) return;
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "SELECT" || tag === "INPUT") return;
    e.preventDefault();
    const idx = slots.findIndex((s) => s.slotUtc === selectedKey);
    const next =
      e.key === "ArrowDown"
        ? Math.min(slots.length - 1, idx + 1)
        : Math.max(0, idx - 1);
    setSelectedKey(slots[next].slotUtc);
  };

  const actions = (
    <div className="flex items-center gap-2">
      <select
        aria-label="Number of slots to fetch"
        value={limit}
        onChange={(e) => setLimit(Number(e.target.value))}
        className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white"
      >
        {LIMIT_OPTIONS.map((n) => (
          <option key={n} value={n}>
            Last {n} slots
          </option>
        ))}
      </select>
      <span
        className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium tabular-nums text-gray-600 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300"
        title="Auto-refreshes every 30 seconds"
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            isLive ? "animate-pulse bg-rp-amber" : "bg-green-500"
          }`}
        />
        {isLive ? "Live · " : ""}Next refresh in {secondsLeft}s
      </span>
      <button
        type="button"
        onClick={() => {
          fetchSlots(limit, Boolean(slots));
          resetCountdown();
        }}
        className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
      >
        Refresh
      </button>
    </div>
  );

  const selectedSlot =
    (slots && slots.find((s) => s.slotUtc === selectedKey)) || latest;

  // The current 30-min slot hasn't been recorded yet if the newest slot is
  // older than the expected (floored-to-30-min) slot for "now".
  const expectedSlotPending =
    !!latest && isExpectedSlotPending(latest.slotUtc, nowMs);
  // Escalate: pending AND more than the grace period past the slot start.
  const oncallEscalation = !!latest && isExpectedSlotLate(latest.slotUtc, nowMs);
  const expectedSlotLabel = formatSlotLabel(floorToSlotUtc(nowMs));

  return (
    <AdminPageLayout
      title="Orchestration Status"
      description="Performance-orchestration run graphs (admin only)"
      actions={actions}
      hideContentScroll
    >
      <div className="h-full" onKeyDown={onKeyDown} role="presentation">
        {loading && !slots ? (
          <LoadingState />
        ) : error && !slots ? (
          <ErrorState message={error} onRetry={() => fetchSlots(limit)} />
        ) : slots && slots.length === 0 ? (
          <EmptyState />
        ) : selectedSlot && slots ? (
          <div className="flex h-full flex-col">
            {oncallEscalation ? (
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
                <span className="flex-none" aria-hidden>
                  ⚠
                </span>
                The orchestration job for the current slot ({expectedSlotLabel}) is over {ONCALL_LATE_THRESHOLD_MIN} minutes late and hasn&apos;t started — contact oncall.
              </div>
            ) : expectedSlotPending ? (
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-rp-amber/40 dark:bg-rp-amber/10 dark:text-rp-amber">
                <span className="h-1.5 w-1.5 flex-none animate-pulse rounded-full bg-rp-amber" />
                Orchestration for the current slot ({expectedSlotLabel}) hasn&apos;t been recorded yet — pending.
              </div>
            ) : null}
            {error && (
              <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                Refresh failed ({error}) — showing last loaded data.
              </div>
            )}
            <div className="flex min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 dark:border-rp-separator">
              <SlotRail
                slots={slots}
                selectedKey={selectedSlot.slotUtc}
                onSelect={setSelectedKey}
                nowMs={nowMs}
              />
              <SlotDag
                slot={selectedSlot}
                isLatest={selectedSlot.slotUtc === latest?.slotUtc}
                nowMs={nowMs}
              />
            </div>
          </div>
        ) : null}
      </div>
    </AdminPageLayout>
  );
};

const LoadingState: React.FC = () => (
  <div className="flex h-full gap-3">
    <div className="w-[268px] flex-none space-y-2.5">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-28 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800"
        />
      ))}
    </div>
    <div className="flex-1 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800" />
  </div>
);

const EmptyState: React.FC = () => (
  <div className="flex h-full items-center justify-center rounded-2xl border border-gray-200 bg-white py-24 dark:border-rp-separator dark:bg-rp-surface">
    <div className="text-center">
      <div className="text-4xl text-gray-300 dark:text-gray-600">🗂️</div>
      <h3 className="mt-3 text-base font-bold text-gray-900 dark:text-white">
        No orchestration runs yet
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Slots appear here once the performance orchestrator records its first run.
      </p>
    </div>
  </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex h-full items-center justify-center rounded-2xl border border-red-200 bg-red-50 py-24 dark:border-red-800 dark:bg-red-900/20">
    <div className="text-center">
      <div className="text-4xl">⚠️</div>
      <h3 className="mt-3 text-base font-bold text-red-700 dark:text-red-400">
        Couldn&apos;t load orchestration status
      </h3>
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-600"
      >
        Retry
      </button>
    </div>
  </div>
);

export default OrchestrationStatusPage;
